import { useUpdateWorkOrderItem } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import {
  WorkOrderItem,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Button, Form, Modal, ModalProps, message } from 'antd';
import { useEffect } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import WorkOrderItemHistoryButton from '../WorkOrderItemHistoryButton';
import WorkOrderItemPartForm, {
  WorkOrderItemFormData,
} from '../WorkOrderItemPartForm';
import WorkOrderItemServiceForm from '../WorkOrderItemServiceForm';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useWorkorderItemPermissions } from '../WorkOrderItemPartForm/workorderItemPermissions';
import { useHistory } from 'react-router-dom';

type Props = Omit<ModalProps, 'onCancel'> & {
  workorderItem?: WorkOrderItem;
  onCancel?: VoidFunction;
};

const WorkOrderItemUpdateModal: React.FC<Props> = ({
  workorderItem,
  ...props
}) => {
  const [form] = Form.useForm<WorkOrderItemFormData>();

  const itemPermissions = useWorkorderItemPermissions();

  const history = useHistory();

  const {
    complement: { data: complement },
    workorder: { data: workOrder },
  } = useWorkorderDetailContext();

  const itemUpdate = useUpdateWorkOrderItem(workOrder?.id, complement?.id);

  useEffect(() => {
    if (workorderItem && props.open) {
      form.setFieldsValue({
        ...workorderItem,
        manufacturerId: workorderItem?.manufacturer?.id,
        itemName: workorderItem.description,
        subAreaId: workorderItem?.subArea?.id,
        supplierId: workorderItem?.supplier?.id,
        discount: workorderItem.discount * 100,
        discountValue:
          workorderItem.unitPrice *
          workorderItem.quantity *
          workorderItem.discount,
        isInternal: String(workorderItem.isInternal),
        type: workorderItem.type,
        isPaintedPart:
          workorderItem.isPaintedPart === null
            ? String(false)
            : String(workorderItem.isPaintedPart),
        employeeId: workorderItem.employee?.id,
      });
    }
  }, [workorderItem, props.open]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (!itemPermissions.allowSellingBellowCostValue) {
        const subTotal = (values.quantity || 0) * (values.unitPrice || 0);

        const amount = subTotal - values.discountValue;

        if (amount < (values.unitCost || 0) * (values.quantity || 0)) {
          message.destroy();
          return message.warn(
            'O preço de venda deve ser maior que o custo unitário',
          );
        }
      }

      if (complement && workorderItem) {
        await itemUpdate.mutateAsync({
          ...values,
          quantity: values.quantity || 1,
          workOrderComplementId: complement.id,
          discount: values.discount ? values.discount / 100 : 0,
          itemId: workorderItem.id,
          isInternal: JSON.parse(values.isInternal),
          isPaintedPart: values.isPaintedPart
            ? JSON.parse(values.isPaintedPart)
            : undefined,
        });

        history.push(`#item-${workorderItem.id}`);

        handleClose();
      }
    } catch {}
  }

  function handleClose() {
    if (props.onCancel) {
      props.onCancel();
    }
  }

  return (
    <Modal
      {...props}
      onCancel={handleClose}
      width={900}
      title={`Atualizar ${
        workorderItem?.type === WorkOrderItemType.Part ? 'peça' : 'serviço'
      }`}
      destroyOnClose={!props.open}
      footer={
        <SpaceForButton justifyContent="end">
          <Button block onClick={handleClose}>
            Cancelar
          </Button>

          {workorderItem?.type === WorkOrderItemType.Part &&
            !!workorderItem &&
            !!complement && (
              <WorkOrderItemHistoryButton
                block
                itemPart={{
                  ...workorderItem,
                  itemName: workorderItem?.description,
                  itemType: workorderItem?.type,
                  complementId: complement?.id,
                  subAreaId: workorderItem?.subArea?.id,
                }}
              />
            )}

          <Button
            block
            type="primary"
            onClick={handleSubmit}
            loading={itemUpdate.isLoading}
          >
            Salvar
          </Button>
        </SpaceForButton>
      }
    >
      <>
        {workorderItem?.type === WorkOrderItemType.Part && (
          <WorkOrderItemPartForm
            form={form}
            isEdit
            workorderItem={workorderItem}
          />
        )}

        {workorderItem?.type === WorkOrderItemType.Service && (
          <WorkOrderItemServiceForm
            form={form}
            isEdit
            workorderItem={workorderItem}
          />
        )}
      </>
    </Modal>
  );
};

export default WorkOrderItemUpdateModal;
